// Phase DELHIVERY-1 (HYBRID) — Courier tab embedded inside Order Detail
// Drawer. Phase DELHIVERY-2 — extended with LTL handover (separate from
// Express), auto-suggestion chip, freight estimate, serviceability check.
// The customer-visible preview is fetched from the public /api/public/track
// endpoint to confirm what the customer is seeing.
import { useEffect, useState } from "react";
import { Truck, Copy, RefreshCw, Download, X as XIcon, ChevronDown, ChevronUp, Eye, Calculator, MapPin } from "lucide-react";
import api from "../../services/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function _relative(ts) {
  if (!ts) return "—";
  const dt = new Date(ts);
  const diff = Date.now() - dt.getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  return dt.toLocaleString("en-IN", { dateStyle: "medium" });
}

// Phase DELHIVERY-2 — pre-handover mini cards.
function FreightEstimateCard({ d }) {
  const [drop, setDrop] = useState(d?.delivery_pincode || d?.delivery_pin || "");
  const [weightKg, setWeightKg] = useState(d?.package_weight || 1);
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const [err, setErr] = useState(null);
  const submit = async () => {
    setBusy(true); setErr(null); setOut(null);
    try {
      const r = await api.post("/api/couriers/delhivery/freight-estimate", {
        pickup_pin: d?.pickup_pincode || d?.pickup_pin || "560001",
        drop_pin: drop,
        weight_kg: parseFloat(weightKg) || 1,
        payment_mode: d?.payment_method === "cod" ? "COD" : "Prepaid",
      });
      setOut(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail?.message || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 rounded-xl p-3.5 bg-white" data-testid="freight-estimate-card">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 font-semibold">
        <Calculator className="h-3.5 w-3.5" /> Freight estimate (LTL)
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        <input value={drop} onChange={(e) => setDrop(e.target.value)} placeholder="Drop PIN"
               data-testid="freight-estimate-drop" className="border border-slate-200 rounded px-2 py-1 text-xs font-mono col-span-1" />
        <input value={weightKg} onChange={(e) => setWeightKg(e.target.value)} placeholder="Weight kg" type="number" step="0.1"
               data-testid="freight-estimate-weight" className="border border-slate-200 rounded px-2 py-1 text-xs col-span-1" />
        <button onClick={submit} disabled={busy} data-testid="freight-estimate-submit"
                className="px-3 py-1 text-xs bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white rounded col-span-1">
          {busy ? "…" : "Estimate"}
        </button>
      </div>
      {out && (
        <div className="mt-2 text-xs text-slate-700" data-testid="freight-estimate-result">
          ₹{out.amount} {out.currency} · ETA {out.eta_days} days
        </div>
      )}
      {err && <div className="mt-2 text-xs text-rose-600">{err}</div>}
    </div>
  );
}

function ServiceabilityCard({ d }) {
  const [drop, setDrop] = useState(d?.delivery_pincode || d?.delivery_pin || "");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(null);
  const submit = async () => {
    setBusy(true); setOk(null);
    try {
      const r = await api.post("/api/couriers/delhivery/serviceability", {
        pickup_pin: d?.pickup_pincode || d?.pickup_pin || "560001",
        drop_pin: drop,
        weight_g: Math.round((parseFloat(d?.package_weight) || 1) * 1000),
      });
      setOk(r.data.serviceable);
    } catch { setOk(false); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 rounded-xl p-3.5 bg-white" data-testid="serviceability-card">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-500 font-semibold">
        <MapPin className="h-3.5 w-3.5" /> Serviceability
      </div>
      <div className="mt-2 flex items-center gap-2">
        <input value={drop} onChange={(e) => setDrop(e.target.value)} placeholder="Drop PIN"
               data-testid="serviceability-drop" className="flex-1 border border-slate-200 rounded px-2 py-1 text-xs font-mono" />
        <button onClick={submit} disabled={busy} data-testid="serviceability-submit"
                className="px-3 py-1 text-xs bg-slate-700 hover:bg-slate-800 disabled:opacity-50 text-white rounded">
          {busy ? "…" : "Check"}
        </button>
      </div>
      {ok === true && <div className="mt-2 text-xs text-emerald-700" data-testid="serviceability-ok">Serviceable ✓</div>}
      {ok === false && <div className="mt-2 text-xs text-rose-700" data-testid="serviceability-no">Not serviceable</div>}
    </div>
  );
}

export default function CourierTab({ d, tid, role, onChanged }) {
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  // Phase DELHIVERY-2 fix · the order-details endpoint now exposes a
  // canonical `courier` block; CourierTab reads from it exclusively.
  // Fallback to top-level `courier_*` fields kept for any legacy callsite
  // that still passes a raw order doc.
  const c = d?.courier || {};
  const orderStatus = d?.order?.status || d?.status;
  const product = c.product
    || (d?.courier_lrn ? "ltl" : (d?.courier_awb ? "express" : null));
  const partnerLabel = c.partner_label
    || (product === "ltl" ? "Delhivery LTL"
        : product === "express" ? "Delhivery Express" : null);
  const identifier = (product === "ltl")
    ? (c.lrn || d?.courier_lrn)
    : (c.awb || d?.courier_awb);
  const status = c.status || d?.courier_status;
  const statusLabel = c.status_label || c.status_display || d?.courier_status_label;
  const lastSynced = c.last_synced_at || d?.courier_last_synced_at;
  const errorsCount = c.errors_count ?? (d?.courier_events?.filter((e) => e.status === "exception")?.length || 0);
  const labelUrl = c.label_url || d?.courier_label_url;
  const initialLabelSize = c.label_size || d?.courier_label_size || "A4";
  const [labelSize, setLabelSize] = useState(initialLabelSize);

  // State machine: prefer the backend-computed flags; fall back to local
  // derivation for legacy raw-order-doc callers.
  const hasShipment = (typeof c.has_shipment === "boolean")
    ? c.has_shipment
    : Boolean(identifier);
  const handoverReady = (typeof c.handover_ready === "boolean")
    ? c.handover_ready
    : (!identifier && ["packing_completed", "ready_for_pickup", "ready_for_handover",
                       "handover_to_main_hub", "packed_at_pickngo"].includes(orderStatus));
  const errors = d?.courier_events?.filter((e) => e.status === "exception") || [];

  const canHandover = handoverReady;
  // Phase DELHIVERY-2-fix-r7 — auto-suggestion chip.
  // BUGFIX: previously read `d.parcels[0].weight_kg` (top-level) which the
  // order-details serializer never emits; the actual path is
  // `d.order.parcels[0].weight_kg`. With every fallback resolving to
  // undefined, `weightKg` was 0 and the chip silently never rendered.
  // Threshold now comes from `main_hub.courier_defaults.auto_suggest_ltl_above_kg`
  // (backfilled to 25 on every hub by add_courier_defaults_to_hubs.py),
  // with a 25 kg hard-coded fallback for safety.
  const weightKg = parseFloat(
    d?.order?.parcels?.[0]?.weight_kg
    ?? d?.order?.weight_kg
    ?? d?.order?.package_weight
    ?? d?.parcels?.[0]?.weight_kg
    ?? d?.package_weight
    ?? ((d?.order?.weight_g ?? d?.weight_g ?? 0) / 1000)
  ) || 0;
  const ltlThresholdKg = d?.main_hub?.courier_defaults?.auto_suggest_ltl_above_kg
    ?? d?.micro_hub?.courier_defaults?.auto_suggest_ltl_above_kg
    ?? 25;
  const ltlSuggested = weightKg >= ltlThresholdKg;
  const recommendedLabel = ltlSuggested ? "Delhivery LTL" : "Delhivery Express";

  const doHandoverExpress = async () => {
    setBusy("handover-express"); setErr(null);
    try {
      await api.post(`/api/courier/create-shipment/${tid}`, { courier_partner: "delhivery" });
      onChanged && (await onChanged());
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const doHandoverLTL = async () => {
    setBusy("handover-ltl"); setErr(null);
    try {
      await api.post(`/api/couriers/delhivery/manifest/${tid}`, { label_size: labelSize });
      onChanged && (await onChanged());
    } catch (e) {
      setErr(e.response?.data?.detail?.message || e.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const doRefresh = async () => {
    setBusy("refresh"); setErr(null);
    try {
      const url = product === "ltl"
        ? `/api/couriers/delhivery/sync/${tid}`
        : `/api/courier/refresh/${tid}`;
      await api.post(url, {});
      onChanged && (await onChanged());
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const doLabel = async () => {
    setBusy("label"); setErr(null);
    try {
      if (product === "ltl") {
        const r = await api.get(`/api/couriers/delhivery/label/${tid}?size=${labelSize}`);
        const url = r.data?.url;
        if (url) window.open(url.startsWith("http") ? url : `${BACKEND}${url}`, "_blank");
      } else {
        const r = await api.get(`/api/courier/label/${tid}`, { responseType: "blob" });
        const url = window.URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url; a.download = `label_${tid}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const doCancel = async () => {
    const reason = window.prompt("Cancellation reason?", "Operator-initiated cancel");
    if (!reason) return;
    setBusy("cancel"); setErr(null);
    try {
      await api.post(`/api/courier/cancel/${tid}`, { reason });
      onChanged && (await onChanged());
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(""); }
  };

  const loadPreview = async () => {
    setPreviewing(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/track/${encodeURIComponent(tid)}`, { cache: "no-store" });
      setPreview(r.ok ? await r.json() : null);
    } catch { setPreview(null); }
    finally { setPreviewing(false); }
  };

  useEffect(() => { if (identifier) loadPreview(); /* eslint-disable-next-line */ }, [identifier]);

  if (canHandover) {
    return (
      <div className="space-y-4" data-testid="order-drawer-courier-tab-pre">
        <div className="border border-orange-200 bg-orange-50/40 rounded-xl p-5">
          <div className="flex items-center gap-2 text-orange-700 flex-wrap">
            <Truck className="h-5 w-5" />
            <h4 className="font-semibold">Ready for courier handover</h4>
            {/* DELHIVERY-2-fix-r7 — chip now renders unconditionally in the
                pre-handover branch. Old test-id kept for back-compat;
                new canonical test-id is `courier-recommendation-chip`. */}
            <span
              className={`ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${ltlSuggested ? "bg-violet-100 text-violet-700 border-violet-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}
              data-testid="courier-recommendation-chip"
              data-recommended-product={ltlSuggested ? "ltl" : "express"}
            >
              Recommended: <strong className="font-bold">{recommendedLabel}</strong>
              <span className="opacity-70 normal-case">
                {ltlSuggested
                  ? `Weight ${weightKg} kg ≥ ${ltlThresholdKg} kg threshold`
                  : `Weight ${weightKg} kg < ${ltlThresholdKg} kg threshold`}
              </span>
            </span>
            <span data-testid="order-drawer-courier-suggestion" className="sr-only">
              Recommended: {ltlSuggested ? "LTL" : "Express"} ({weightKg} kg)
            </span>
          </div>
          <p className="text-sm text-orange-600/80 mt-2">
            Pick a product below. Express is for small/single-piece. LTL is for
            bulk / heavy. The customer-facing public page never shows the AWB or LRN.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button onClick={doHandoverExpress} disabled={busy === "handover-express"}
                    data-testid="order-drawer-courier-handover-express-btn"
                    className="px-4 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2">
              <Truck className="h-4 w-4" />
              {busy === "handover-express" ? "Handing over…" : "Handover via Delhivery Express"}
            </button>
            <button onClick={doHandoverLTL} disabled={busy === "handover-ltl"}
                    data-testid="order-drawer-courier-handover-ltl-btn"
                    className="px-4 py-2.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2">
              <Truck className="h-4 w-4" />
              {busy === "handover-ltl" ? "Handing over…" : "Handover via Delhivery LTL"}
            </button>
          </div>
          {err && <div className="text-xs text-rose-600 mt-3" data-testid="order-drawer-courier-error">
            {typeof err === "string" ? err : JSON.stringify(err)}
          </div>}
        </div>

        {/* Phase DELHIVERY-2 — quick-look helpers. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FreightEstimateCard d={d} />
          <ServiceabilityCard d={d} />
        </div>
      </div>
    );
  }

  if (!hasShipment) {
    return (
      <div className="text-sm text-slate-500" data-testid="order-drawer-courier-tab-not-ready">
        Parcel is not yet at the courier-handover stage. Status: <code className="bg-slate-50 px-1.5 py-0.5 rounded">{orderStatus || "—"}</code>
      </div>
    );
  }

  // Populated view
  const productPillCn = product === "ltl"
    ? "bg-violet-50 text-violet-700 border-violet-200"
    : "bg-orange-50 text-orange-700 border-orange-200";
  const idLabel = product === "ltl" ? "LRN" : "AWB";
  return (
    <div className="space-y-4" data-testid="order-drawer-courier-tab">
      <div className="border border-slate-200 rounded-xl p-4 bg-white">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-slate-800">
            <Truck className="h-4 w-4 text-violet-500" />
            <span className="font-semibold">{partnerLabel}</span>
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${productPillCn}`}
                  data-testid="order-drawer-courier-product-pill">
              {product === "ltl" ? "LTL" : "Express"}
            </span>
            <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200">
              {statusLabel || status || "in transit"}
            </span>
          </div>
          <span className="text-xs text-slate-400">Last synced {_relative(lastSynced)}</span>
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{idLabel}</span>
          <code className="text-xs font-mono bg-slate-50 px-2 py-1 rounded border border-slate-200"
                data-testid={`order-drawer-courier-${idLabel.toLowerCase()}`}>{identifier}</code>
          <button onClick={() => navigator.clipboard?.writeText(identifier)}
                  data-testid={`order-drawer-courier-${idLabel.toLowerCase()}-copy`}
                  title={`Copy ${idLabel}`}
                  className="p-1.5 hover:bg-slate-100 rounded text-slate-400">
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Action buttons + LTL-only label size toggle */}
      {product === "ltl" && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Label size:</span>
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid="order-drawer-courier-label-size">
            {["A4", "A6"].map((s) => (
              <button key={s} onClick={() => setLabelSize(s)}
                      data-testid={`order-drawer-courier-label-size-${s}`}
                      className={`px-2.5 py-1 ${labelSize === s ? "bg-violet-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <button onClick={doRefresh} disabled={busy === "refresh"}
                data-testid="order-drawer-courier-refresh-btn"
                className="px-3 py-2 border border-slate-200 hover:bg-slate-50 disabled:opacity-50 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 justify-center">
          <RefreshCw className={`h-3.5 w-3.5 ${busy === "refresh" ? "animate-spin" : ""}`} /> Re-sync
        </button>
        <button onClick={doLabel} disabled={busy === "label"}
                data-testid="order-drawer-courier-label-btn"
                className="px-3 py-2 border border-slate-200 hover:bg-slate-50 disabled:opacity-50 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 justify-center">
          <Download className="h-3.5 w-3.5" /> Label{product === "ltl" ? ` ${labelSize}` : " PDF"}
        </button>
        <button onClick={doCancel} disabled={busy === "cancel" || status === "cancelled"}
                data-testid="order-drawer-courier-cancel-btn"
                className="px-3 py-2 border border-rose-200 text-rose-700 hover:bg-rose-50 disabled:opacity-50 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 justify-center">
          <XIcon className="h-3.5 w-3.5" /> Cancel
        </button>
      </div>
      {err && <div className="text-xs text-rose-600" data-testid="order-drawer-courier-error">
        {typeof err === "string" ? err : JSON.stringify(err)}
      </div>}

      {/* API errors sub-card */}
      <div className="border border-slate-200 rounded-xl bg-white">
        <button onClick={() => setShowErrors((v) => !v)}
                data-testid="order-drawer-courier-errors-toggle"
                className="w-full px-4 py-2.5 flex items-center justify-between text-left">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">API errors</span>
          <span className="text-xs text-slate-400 inline-flex items-center gap-1">
            {errors.length} event{errors.length !== 1 ? "s" : ""}
            {showErrors ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </span>
        </button>
        {showErrors && (
          <div className="px-4 pb-3 text-xs text-slate-600">
            {errors.length === 0 ? (
              <span className="text-slate-400">No errors recorded.</span>
            ) : errors.map((e, i) => (
              <div key={i} className="py-1 border-b border-slate-100 last:border-0">
                <span className="text-slate-400 mr-2">{_relative(e.ts)}</span>
                {e.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Customer-facing preview */}
      <div className="border border-slate-200 rounded-xl bg-white">
        <div className="px-4 py-2.5 flex items-center justify-between border-b border-slate-100">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1.5">
            <Eye className="h-3.5 w-3.5" /> What the customer sees
          </span>
          <button onClick={loadPreview} title="Refresh preview"
                  data-testid="order-drawer-courier-preview-refresh"
                  className="p-1 hover:bg-slate-100 rounded text-slate-400">
            <RefreshCw className={`h-3 w-3 ${previewing ? "animate-spin" : ""}`} />
          </button>
        </div>
        {!preview && (
          <div className="p-4 text-xs text-slate-400" data-testid="order-drawer-courier-preview-empty">
            Loading…
          </div>
        )}
        {preview && (
          <div className="p-4 space-y-2" data-testid="order-drawer-courier-preview">
            <div className="font-semibold text-slate-800">{preview.current_status_display}</div>
            {preview.courier_partner && (
              <div className="text-xs text-violet-700">via {preview.courier_partner}</div>
            )}
            {preview.timeline?.length > 0 && (
              <ul className="mt-2 text-xs text-slate-600 space-y-1">
                {preview.timeline.slice(-4).reverse().map((ev, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${ev.source === "courier" ? "bg-violet-500" : "bg-orange-500"}`} />
                    <span>{ev.label} <span className="text-slate-400">· {_relative(ev.timestamp)}</span></span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
